if (!customElements.get('gift-finder')) {
  customElements.define('gift-finder', class GiftFinder extends HTMLElement {
    connectedCallback() {
      this.mobileViewport ||= window.matchMedia('(max-width: 749px)');
      this.onViewportChange ||= () => this.updateTransparentHeader();
      this.mobileViewport.addEventListener('change', this.onViewportChange);
      this.onBlockSelect ||= (event) => this.previewBlock(event.target);
      if (window.Shopify?.designMode) this.addEventListener('shopify:block:select', this.onBlockSelect);
      this.updateTransparentHeader();
      if (this.initialized) return;
      this.initialized = true;
      this.steps = [...this.querySelectorAll('[data-step]')];
      this.stepIndex = 0;
      this.answers = Object.fromEntries(this.steps.map((step) => [step.dataset.step, []]));
      this.filters = {};
      this.querySelector('[data-step-total]').textContent = String(this.steps.length).padStart(2, '0');
      this.mountStepMedia();
      this.addEventListener('click', (event) => this.onClick(event));
      if (!this.steps.length) {
        this.showView('welcome');
        this.showError(this.dataset.unavailable);
        this.querySelector('[data-retry]').hidden = true;
        return;
      }
      this.initialize();
    }

    disconnectedCallback() {
      this.mobileViewport?.removeEventListener('change', this.onViewportChange);
      this.removeEventListener('shopify:block:select', this.onBlockSelect);
      this.controller?.abort();
    }

    mountStepMedia() {
      const container = this.querySelector('.gift-finder__media');
      const welcomeImage = container.querySelector('[data-step-media="welcome"] img');
      this.querySelectorAll('template[data-step-picture]').forEach((template) => {
        const visual = template.content.firstElementChild.cloneNode(true);
        if (template.hasAttribute('data-desktop-fallback') && welcomeImage) {
          const fallback = welcomeImage.cloneNode(true);
          fallback.loading = 'lazy';
          fallback.fetchPriority = 'auto';
          visual.querySelector('img').replaceWith(fallback);
        }
        container.append(visual);
      });
    }

    previewBlock(target) {
      const step = target.closest('[data-step]');
      if (step && this.steps.includes(step)) {
        this.editorStep = step;
        this.showView('quiz');
        this.showStep(this.steps.indexOf(step), false);
      } else if (target.closest('[data-step-media="welcome"]')) {
        this.editorStep = 'welcome';
        this.showView('welcome');
      }
    }

    normalize(value) {
      return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]/g, '');
    }

    async initialize() {
      this.ready = false;
      const params = new URL(window.location.href).searchParams;
      this.showView(this.steps.some((step) => params.has(`gift_${step.dataset.step}`)) ? 'loading' : 'welcome');
      this.unisexFilterValue = null;
      this.setBusy(true);
      this.querySelector('[data-fallback]').hidden = true;
      try {
        const response = await this.fetchSelection(new URLSearchParams());
        const filterRoot = response.querySelector('[data-gift-filters]').content;
        for (const step of ['recipient', 'type', 'material', 'territory']) {
          if (!this.answers[step]) continue;
          const param = this.dataset[`${step}Filter`];
          const filter = [...filterRoot.querySelectorAll('[data-param]')].find((item) => item.dataset.param === param);
          if (!filter && !['material', 'territory'].includes(step)) throw new Error('Missing filter');
          this.filters[step] = param;
          if (step === 'recipient') {
            const unisexLabels = [this.dataset.unisexFilterLabel, this.dataset.unisexFilterLabelTranslated]
              .filter(Boolean).map((label) => this.normalize(label));
            const unisexValue = [...filter.querySelectorAll('[data-value]')]
              .find((item) => unisexLabels.includes(this.normalize(item.dataset.label)));
            this.unisexFilterValue = unisexValue?.dataset.value || null;
          }
          let available = 0;
          this.querySelectorAll(`[data-step="${step}"] [data-filter-label]`).forEach((button) => {
            const answerFilter = button.dataset.filterParam
              ? [...filterRoot.querySelectorAll('[data-param]')].find((item) => item.dataset.param === button.dataset.filterParam)
              : filter;
            const labels = [button.dataset.filterLabel, button.dataset.filterLabelTranslated, button.textContent]
              .filter(Boolean).map((label) => this.normalize(label));
            const values = [...(answerFilter?.querySelectorAll('[data-value]') || [])].filter((item) => {
              if (labels.includes(this.normalize(item.dataset.label))) return true;
              if (!button.hasAttribute('data-filter-prefix')) return false;
              const words = (value) => value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
              const prefix = words(button.dataset.filterLabel);
              const label = words(item.dataset.label);
              return prefix && (label.startsWith(`${prefix} `) || (label.startsWith(prefix) && /^\d/.test(label.slice(prefix.length))));
            });
            button.disabled = !values.length;
            button.dataset.filterValues = JSON.stringify([...new Set(values.map((value) => value.dataset.value))]);
            if (values.length) {
              available += 1;
            }
          });
          if (!available && !['material', 'territory'].includes(step)) throw new Error('Missing answers');
        }
        if (!filterRoot.querySelector('[data-filter-type="price_range"]')) throw new Error('Missing price filter');
        this.ready = true;
        this.querySelector('[data-start]').disabled = false;
        this.showStep(0, false);
      } catch (error) {
        if (error.name !== 'AbortError') this.showError(this.dataset.unavailable);
      } finally {
        this.setBusy(false);
      }
      if (this.ready) await this.restoreFromUrl();
      if (this.editorStep === 'welcome') this.showView('welcome');
      else if (this.editorStep) this.previewBlock(this.editorStep);
    }

    async restoreFromUrl() {
      const params = new URL(window.location.href).searchParams;
      const hasSelection = this.steps.some((step) => params.has(`gift_${step.dataset.step}`));
      if (!hasSelection) return;
      this.steps.forEach((step) => {
        const name = step.dataset.step;
        const allowed = [...step.querySelectorAll('[data-answer]')]
          .filter((button) => !button.disabled).map((button) => button.dataset.answer);
        const values = [...new Set(params.getAll(`gift_${name}`).flatMap((value) => value.split(',')))]
          .filter((value) => allowed.includes(value));
        this.answers[name] = name === 'type' && !values.includes('any') ? values : values.slice(0, 1);
        if (name === 'type' && values.includes('any')) this.answers.type = ['any'];
      });
      this.updateUrl();
      const missing = this.steps.findIndex((step) => !this.answers[step.dataset.step].length);
      this.showStep(missing === -1 ? this.steps.length - 1 : missing, false);
      if (missing === -1) {
        this.showView('loading');
        await this.showResults(false);
      } else {
        this.showView('quiz');
      }
    }

    selectionUrl(url = new URL(window.location.href)) {
      url.searchParams.delete('gift_metal');
      this.steps.forEach((step) => {
        const name = step.dataset.step;
        const param = `gift_${name}`;
        url.searchParams.delete(param);
        if (this.answers[name].length) url.searchParams.set(param, this.answers[name].join(','));
      });
      return url;
    }

    updateUrl() {
      const url = this.selectionUrl();
      if (url.href !== window.location.href) {
        // History may be restricted in an embedded theme preview; keep the quiz usable.
        try {
          window.history.replaceState(window.history.state, '', url.href);
        } catch (error) {
          // The copy button can still build a shareable URL from the current answers.
        }
      }
      this.querySelector('[data-share-status]').textContent = '';
      this.querySelector('[data-share-fallback]').hidden = true;
    }

    async copySelectionLink() {
      const button = this.querySelector('[data-share]');
      const status = this.querySelector('[data-share-status]');
      const fallback = this.querySelector('[data-share-fallback]');
      const url = this.selectionUrl(new URL(window.location.pathname, window.location.origin)).href;
      button.disabled = true;
      fallback.hidden = true;
      try {
        await navigator.clipboard.writeText(url);
        status.textContent = button.dataset.copied;
      } catch (error) {
        status.textContent = button.dataset.copyFallback;
        fallback.hidden = false;
        const input = fallback.querySelector('input');
        input.value = url;
        input.focus({ preventScroll: true });
        input.select();
      } finally {
        button.disabled = false;
      }
    }

    onClick(event) {
      const button = event.target.closest('button');
      if (!button || !this.contains(button) || button.disabled || this.busy) return;
      if (button.matches('[data-start]')) {
        this.showView('quiz');
        this.showStep(0);
      } else if (button.matches('[data-retry]')) {
        this.ready ? this.showResults() : this.initialize();
      } else if (button.matches('[data-share]')) {
        this.copySelectionLink();
      } else if (button.matches('[data-edit]')) {
        this.showView('quiz');
        this.showStep(0);
      } else if (button.matches('[data-back]')) {
        this.showStep(this.stepIndex - 1);
      } else if (button.matches('[data-next]')) {
        const step = this.steps[this.stepIndex].dataset.step;
        if (!this.answers[step].length) return;
        if (this.stepIndex === this.steps.length - 1) this.showResults();
        else this.showStep(this.stepIndex + 1);
      } else if (button.matches('[data-answer]')) {
        const step = this.steps[this.stepIndex].dataset.step;
        const answer = button.dataset.answer;
        if (step === 'type') {
          if (answer === 'any') {
            this.answers.type = ['any'];
          } else {
            const selected = this.answers.type.filter((value) => value !== 'any');
            this.answers.type = selected.includes(answer) ? selected.filter((value) => value !== answer) : [...selected, answer];
          }
        } else {
          this.answers[step] = [answer];
        }
        this.updateUrl();
        this.updateButtons();
        if (this.stepIndex === this.steps.length - 1) this.showResults();
        else if (step !== 'type') this.showStep(this.stepIndex + 1);
      }
    }

    updateButtons() {
      this.steps.forEach((step) => {
        step.querySelectorAll('[data-answer]').forEach((button) => {
          button.setAttribute('aria-pressed', String(this.answers[step.dataset.step].includes(button.dataset.answer)));
        });
      });
      const step = this.steps[this.stepIndex].dataset.step;
      const next = this.querySelector('[data-next]');
      const hasAnswer = this.answers[step].length > 0;
      next.hidden = !hasAnswer;
      next.disabled = !hasAnswer;
      next.textContent = this.stepIndex === this.steps.length - 1 ? next.dataset.resultsLabel : next.dataset.nextLabel;
    }

    updateTransparentHeader() {
      const setting = this.mobileViewport.matches ? 'data-mobile-transparent-header' : 'data-cover-transparent-header';
      this.toggleAttribute('data-transparent-header', ['welcome', 'quiz'].includes(this.dataset.view) && this.hasAttribute(setting));
    }

    showView(view) {
      this.dataset.view = view;
      this.updateTransparentHeader();
      this.querySelector('[data-shell]').hidden = view === 'results' || view === 'loading';
      this.querySelector('[data-welcome]').hidden = view !== 'welcome';
      this.querySelector('[data-questionnaire]').hidden = view !== 'quiz';
      this.querySelector('[data-results]').hidden = view !== 'results';
      this.updateStepMedia();
    }

    showStep(index, focus = true) {
      this.stepIndex = Math.max(0, Math.min(index, this.steps.length - 1));
      this.steps.forEach((step, i) => {
        step.setAttribute('aria-hidden', String(i !== this.stepIndex));
        step.inert = i !== this.stepIndex;
      });
      this.querySelector('[data-back]').hidden = this.stepIndex === 0;
      this.querySelector('[data-step-number]').textContent = String(this.stepIndex + 1).padStart(2, '0');
      this.querySelector('[data-progress]').style.width = `${((this.stepIndex + 1) / this.steps.length) * 100}%`;
      this.updateStepMedia();
      this.updateButtons();
      if (focus) {
        this.steps[this.stepIndex].querySelector('h2').focus({ preventScroll: true });
      }
    }

    updateStepMedia() {
      const name = this.dataset.view === 'quiz' ? this.steps[this.stepIndex]?.dataset.step : 'welcome';
      const media = [...this.querySelectorAll('[data-step-media]')];
      const selected = media.find((item) => item.dataset.stepMedia === name)
        || media.find((item) => item.dataset.stepMedia === 'welcome');
      const quiz = this.dataset.view === 'quiz';
      const firstStep = this.steps[0]?.dataset.step;
      for (const item of media) {
        if (quiz || (this.dataset.view === 'welcome' && item.dataset.stepMedia === firstStep)) {
          const image = item.querySelector('img');
          if (image && image.loading !== 'eager') image.loading = 'eager';
        }
      }

      this.requestedMedia = selected;
      const reveal = () => {
        if (this.requestedMedia === selected) {
          media.forEach((item) => { item.hidden = item !== selected; });
        }
      };
      const image = selected?.querySelector('img');
      if (quiz && selected?.hidden && image?.decode) {
        image.decode().then(reveal, () => {});
      } else {
        reveal();
      }
    }

    buildParams() {
      const params = new URLSearchParams({ sort_by: this.dataset.sort });
      for (const step of ['recipient', 'type', 'material', 'territory']) {
        (this.answers[step] || []).filter((answer) => answer !== 'any').forEach((answer) => {
          const button = this.querySelector(`[data-step="${step}"] [data-answer="${answer}"]`);
          const param = button.dataset.filterParam || this.filters[step];
          JSON.parse(button.dataset.filterValues).forEach((value) => {
            if (!params.getAll(param).includes(value)) params.append(param, value);
          });
        });
      }
      if (this.unisexFilterValue && this.answers.recipient?.some((answer) => answer === 'woman' || answer === 'man')) {
        if (!params.getAll(this.filters.recipient).includes(this.unisexFilterValue)) {
          params.append(this.filters.recipient, this.unisexFilterValue);
        }
      }
      const ranges = { under_50: [null, '50'], under_100: [null, '100'], under_150: [null, '150'], under_200: [null, '200'], over_200: ['200.01', null] };
      const [min, max] = ranges[this.answers.budget?.[0]] || [null, null];
      if (min !== null) params.set('filter.v.price.gte', min);
      if (max !== null) params.set('filter.v.price.lte', max);
      return params;
    }

    async fetchSelection(params) {
      this.controller?.abort();
      this.controller = new AbortController();
      const url = new URL(this.dataset.collectionUrl, window.location.origin);
      url.search = params.toString();
      url.searchParams.set('section_id', 'gift-finder-results');
      const timeout = setTimeout(() => this.controller?.abort(), 15000);
      try {
        const response = await fetch(url, { signal: this.controller.signal });
        if (!response.ok) throw new Error('Request failed');
        const document = new DOMParser().parseFromString(await response.text(), 'text/html');
        const result = document.querySelector('[data-gift-response]');
        if (result?.dataset.valid !== 'true' || !result.querySelector('[data-gift-grid]')) throw new Error('Invalid collection response');
        return result;
      } catch (error) {
        if (error.name === 'AbortError' && this.isConnected) throw new Error('Request timed out');
        throw error;
      } finally {
        clearTimeout(timeout);
      }
    }

    async fetchProducts(params) {
      const pageParams = new URLSearchParams(params);
      const limit = Number(this.dataset.limit);
      const seen = new Set();
      let result;
      let grid;
      let nextPage;
      do {
        const page = await this.fetchSelection(pageParams);
        if (!result) {
          result = page;
          grid = page.querySelector('[data-gift-grid]');
        }
        const products = [...page.querySelectorAll('[data-gift-product]')];
        products.forEach((product) => product.remove());
        products.forEach((product) => {
          const ids = product.dataset.linkedIds.split(',');
          product.remove();
          if (grid.querySelectorAll('[data-gift-product]').length >= limit || ids.some((id) => seen.has(id))) return;
          ids.forEach((id) => seen.add(id));
          grid.append(product);
        });
        nextPage = page.querySelector('[data-next-page]')?.dataset.nextPage;
        if (nextPage) pageParams.set('page', nextPage);
      } while (nextPage && grid.querySelectorAll('[data-gift-product]').length < limit);
      grid.querySelectorAll('template').forEach((template) => template.remove());
      return result;
    }

    async showResults(focus = true) {
      if (!Object.values(this.answers).every((answers) => answers.length)) return;
      this.setBusy(true);
      this.querySelector('[data-fallback]').hidden = true;
      try {
        const params = this.buildParams();
        const result = await this.fetchProducts(params);
        const grid = result.querySelector('[data-gift-grid]');
        this.querySelector('[data-products]').replaceChildren(grid);
        this.querySelector('[data-summary]').textContent = this.steps.map((step) => {
          return this.answers[step.dataset.step].map((answer) => step.querySelector(`[data-answer="${answer}"]`).textContent.trim()).join(', ');
        }).join(' · ');
        this.querySelector('[data-empty]').hidden = !!grid.children.length;
        const heading = this.querySelector('[data-results] h2');
        heading.textContent = grid.children.length ? heading.dataset.title : heading.dataset.emptyTitle;
        this.showView('results');
        if (focus) heading.focus({ preventScroll: true });
      } catch (error) {
        if (error.name !== 'AbortError') this.showError(this.dataset.error);
      } finally {
        this.setBusy(false);
      }
    }

    setBusy(busy) {
      this.busy = busy;
      this.querySelector('[data-start]').disabled = busy || !this.ready;
      this.setAttribute('aria-busy', String(busy));
      if (busy) {
        this.querySelector('[data-status]').textContent = this.dataset.loading;
        this.querySelector('[data-retry]').hidden = true;
      } else if (this.querySelector('[data-retry]').hidden) {
        this.querySelector('[data-status]').textContent = '';
      }
    }

    showError(message) {
      if (this.dataset.view === 'loading') this.showView(this.ready ? 'quiz' : 'welcome');
      this.querySelector('[data-status]').textContent = message;
      this.querySelector('[data-retry]').hidden = false;
      this.querySelector('[data-fallback]').hidden = false;
    }
  });
}
